/*
This code was adapted from https://github.com/adaydesign/react-chakra-ui-table-v2

It was converted from TypeScript to JavaScript and enhanced to support pagination, sorting, and filtering.
*/

import React from "react"
import {
  Table as CKTable,
  Thead,
  Tbody,
  Tr,
  Td,
  Th,
  HStack,
  Button,
  IconButton,
  Select,
  Text,
  Input,
  Flex,
  TableContainer,
  Checkbox,
  MenuButton,
  MenuList,
  VStack,
  Box,
  Spinner,
  CheckboxGroup,
  Menu,
  Alert,
  AlertTitle,
  AlertIcon,
} from "@chakra-ui/react"

import {
  ArrowBackIcon,
  ArrowForwardIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DeleteIcon,
  Search2Icon,
  SearchIcon,
  TriangleDownIcon,
  TriangleUpIcon,
} from "@chakra-ui/icons"
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getFilteredRowModel,
} from "@tanstack/react-table"
import { FaFile, FaTrash, FaDownload, FaGripVertical } from "react-icons/fa6"
import { SingleDatepicker } from "chakra-dayzed-datepicker"
import { Link } from "@chakra-ui/react"
import { getNumformat } from "@/lib/formatters"
import UploadRecords from "./UploadRecords"
import { Pagination, Limit, Offset, SchemaRecordCounts } from "../lib/store"

export const DEFAULT_PAGES = [25, 50, 100]

export const NoDataDisplay = () => {
  return (
    <Flex
      direction="column"
      p="2"
      gap="3"
      borderRadius="8"
      alignContent="left"
      bgColor="white"
      m="auto"
    >
      <Alert width="100%">
        <Flex direction={{ base: "column", md: "row" }} gap="4" width="100%">
          <Box
            width={{ base: "100%", md: "50%" }}
            position="relative"
            paddingBottom={{ base: "56.25%", md: "28.125%" }}
          >
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/RpZPYvfUwg4"
              title="Blind Insight Tutorial"
              style={{
                border: "none",
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Box>
          <VStack
            justifyContent="space-between"
            alignItems="flex-start"
            width={{ base: "100%", md: "50%" }}
            spacing={2}
          >
            <VStack>
              <HStack width="100%" flexWrap="wrap">
                <AlertIcon flexShrink={0} />
                <AlertTitle
                  maxWidth="calc(100% - 40px)"
                  whiteSpace="normal"
                  overflowWrap="break-word"
                >
                  Looks like you need to add some data!
                </AlertTitle>
              </HStack>
              <Text
                variant="base"
                width="100%"
                whiteSpace="normal"
                overflowWrap="break-word"
              >
                Only encrypted data ever enters or exits Blind Insight, and we
                don&apos;t hold any keys. Download the Blind Proxy to encrypt
                data in your own environment before sending to Blind Insight.
                Soon, the Blind Proxy will be available as a browser extension,
                mobile library, and via secure enclave.
              </Text>
            </VStack>
            <Flex
              width="full"
              direction={{ base: "column", md: "row" }}
              justifyContent="right"
              gap={{ base: "1", md: "4" }}
              flexWrap="wrap"
            >
              <Link
                href="https://github.com/blind-insight/demo-datasets"
                isExternal
                rel="noreferrer"
              >
                <Button
                  variant="secondary"
                  title="Download a sample dataset"
                  size={{ base: "sm", md: "md" }}
                  rightIcon={<FaFile />}
                >
                  Download a sample dataset
                </Button>
              </Link>
              <Link
                href="https://docs.blindinsight.io/download/"
                isExternal
                rel="noreferrer"
              >
                <Button
                  variant="primary"
                  title="Download the Blind Proxy"
                  size={{ base: "sm", md: "md" }}
                  rightIcon={<FaDownload />}
                >
                  Download the Blind Proxy
                </Button>
              </Link>
            </Flex>
          </VStack>
        </Flex>
      </Alert>
    </Flex>
  )
}

export const AllRecordsFilteredOutDisplay = () => {
  return (
    <Flex
      direction="column"
      p="2"
      gap="3"
      borderRadius="8"
      alignContent="left"
      bgColor="white"
      m="auto"
    >
      <Alert width="100%">
        <VStack>
          <HStack width="100%" flexWrap="wrap">
            <AlertIcon flexShrink={0} />
            <AlertTitle
              maxWidth="calc(100% - 40px)"
              whiteSpace="normal"
              overflowWrap="break-word"
            >
              No records match your filters.
            </AlertTitle>
          </HStack>
          <Text
            variant="base"
            width="100%"
            whiteSpace="normal"
            overflowWrap="break-word"
          >
            Try adjusting your filters to see more records.
          </Text>
        </VStack>
      </Alert>
    </Flex>
  )
}

export const HasProxyDataDisplay = ({
  uploadState,
  onUploadStateChange,
  onUpdateRecords,
  fileDropProps,
}) => {
  return (
    <Box w="100%" h="100%" overflowY="hidden">
      <Alert h="auto" flexGrow={1}>
        <Flex
          flexDirection={{ base: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="flex-start"
          gap="8"
          minH="100%"
        >
          <VStack
            id="info-stack"
            justifyContent="space-between"
            flex="1"
            alignItems="stretch"
            spacing="4"
          >
            <Flex direction="column" gap="3">
              <HStack>
                <AlertIcon />
                <AlertTitle
                  maxWidth="calc(100% - 40px)"
                  whiteSpace="normal"
                  overflowWrap="break-word"
                >
                  Looks like you need to add some data!
                </AlertTitle>
              </HStack>
              <Text
                variant="base"
                width="100%"
                whiteSpace="normal"
                overflowWrap="break-word"
              >
                Congratulations! You are running the Blind Proxy. You are ready
                to start uploading. Your data must be in JSON or CSV format.{" "}
                <Link
                  href="https://docs.blindinsight.io/overview/what-is-blindinsight/#records"
                  isExternal
                  rel="noreferrer"
                  textDecoration="underline"
                  _hover={{ textDecoration: "none" }}
                >
                  Please see the documentation on how to format it.
                </Link>
              </Text>
              <Text
                variant="base"
                width="100%"
                whiteSpace="normal"
                overflowWrap="break-word"
              >
                Then when you&apos;re ready, go ahead and drag and drop your
                file into the box on the right and click <b>Upload</b>.
              </Text>
            </Flex>
            <Flex w="100%" justify="flex-end" mt="auto">
              <Link
                href="https://github.com/blind-insight/demo-datasets"
                isExternal
                rel="noreferrer"
              >
                <Button
                  variant="secondary"
                  title="Download a sample dataset"
                  size={{ base: "sm", lg: "md" }}
                  rightIcon={<FaFile />}
                >
                  Download a sample dataset
                </Button>
              </Link>
            </Flex>
          </VStack>
          <Box
            flex="1"
            minW={{ base: "100%", md: "30em" }}
            maxW={{ base: "100%", md: "30em" }}
            position="relative"
          >
            <UploadRecords
              uploadState={uploadState}
              onUploadStateChange={onUploadStateChange}
              onUpdateRecords={onUpdateRecords}
              fileDropProps={fileDropProps}
            />
          </Box>
        </Flex>
      </Alert>
    </Box>
  )
}

export const LoadingDataDisplay = () => {
  return (
    <Flex
      direction="column"
      p={4}
      align="center"
      justify="center"
      bgColor="white"
    >
      <Spinner
        size="xl"
        boxSize="70px"
        thickness="0.25rem"
        mb={3}
        color="Gray.400"
      />
      <Text>Loading Data</Text>
    </Flex>
  )
}

export function DataTable({
  EncryptButton,
  hasProxy,
  data = [],
  columns,
  initialSortingState = [],
  initialColumnVisibility = {},
  filterIsOpen = false,
  rowCount = -1,
  hasMore = false,
  uploadState,
  onUploadStateChange,
  onUpdateRecords,
  fileInputRef,
  selectedFile,
  onFileChange,
  onFileDrop,
  onOpenAttachment,
  onClearFile,
  onPageChange,
  onSelectionChange,
  onDeleteRow,
  schemaId,
  isLoading = false,
  isCountLoading = false,
}) {
  const [sorting, setSorting] = React.useState(initialSortingState)
  const [rowSelection, setRowSelection] = React.useState({})
  const [columnVisibility, setColumnVisibility] = React.useState(
    initialColumnVisibility,
  )
  const [limit, setLimit] = Limit.useState()
  const [offset, setOffset] = Offset.useState()
  const [, setPagination] = Pagination.useState()

  // Column ordering state — reset whenever the column list changes (e.g. schema switch)
  const [columnOrder, setColumnOrder] = React.useState(() =>
    (columns || []).map((col) => col.id ?? col.accessorKey),
  )
  React.useEffect(() => {
    setColumnOrder((columns || []).map((col) => col.id ?? col.accessorKey))
  }, [columns])

  // Drag-and-drop reorder state
  const dragColumnId = React.useRef(null)
  const [dragOverColumnId, setDragOverColumnId] = React.useState(null)

  // Column resize — track container width at first resize so unresized columns
  // keep their visual width and the table grows wider instead of squeezing them
  const tableContainerRef = React.useRef(null)
  const baseColWidthRef = React.useRef(null)

  const handlePageSizeChange = React.useCallback(
    (e) => {
      const newSize = Number(e.target.value)
      const currentStartRow = offset
      const newPageIndex = Math.floor(currentStartRow / newSize)
      const newOffset = newPageIndex * newSize

      setLimit(newSize)
      setOffset(newOffset)
      setPagination({
        pageIndex: newPageIndex,
        pageSize: newSize,
      })
    },
    [offset, setLimit, setOffset, setPagination],
  )

  const handlePaginationChange = React.useCallback(
    (newPagination) => {
      const newLimit = Number(newPagination.pageSize)
      const newPageIndex = Number(newPagination.pageIndex)
      const newOffset = newPageIndex * newLimit

      if (!isNaN(newLimit) && !isNaN(newOffset)) {
        setLimit(newLimit)
        setOffset(newOffset)
        setPagination({
          pageIndex: newPageIndex,
          pageSize: newLimit,
        })
        if (typeof onPageChange === "function") {
          onPageChange({
            pageIndex: newPageIndex,
            pageSize: newLimit,
            offset: newOffset,
            limit: newLimit,
          })
        }
      }
    },
    [setLimit, setOffset, setPagination, onPageChange],
  )

  const selectionColumn = React.useMemo(
    () => ({
      id: "__select__",
      size: 36,
      enableResizing: false,
      header: function SelectionHeader({ table: t }) {
        return (
          <Checkbox
            isChecked={t.getIsAllPageRowsSelected()}
            isIndeterminate={t.getIsSomePageRowsSelected()}
            onChange={t.getToggleAllPageRowsSelectedHandler()}
            aria-label="Select all rows on this page"
            size="sm"
          />
        )
      },
      cell: function SelectionCell({ row }) {
        return (
          <Checkbox
            isChecked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            aria-label="Select row"
            size="sm"
            onClick={(e) => e.stopPropagation()}
          />
        )
      },
    }),
    [],
  )

  const columnsWithSelection = React.useMemo(
    () =>
      onSelectionChange ? [selectionColumn, ...(columns || [])] : columns || [],
    [columns, onSelectionChange, selectionColumn],
  )

  const deleteColumn = React.useMemo(
    () => ({
      id: "__delete__",
      size: 36,
      enableResizing: false,
      header: function DeleteHeader() {
        return null
      },
      cell: function DeleteCell({ row }) {
        return (
          <Flex justifyContent="flex-end">
            <IconButton
              aria-label={`Delete record ${row.original.id}`}
              icon={<DeleteIcon />}
              size="xs"
              colorScheme="red"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                onDeleteRow?.(row.original.id)
              }}
            />
          </Flex>
        )
      },
    }),
    [onDeleteRow],
  )

  const columnsWithEnhancements = React.useMemo(
    () =>
      onDeleteRow
        ? [...columnsWithSelection, deleteColumn]
        : columnsWithSelection,
    [columnsWithSelection, deleteColumn, onDeleteRow],
  )

  const table = useReactTable({
    columns: columnsWithEnhancements,
    data: data || [],
    initialState: {
      pagination: {
        pageIndex: Math.floor(offset / limit),
        pageSize: limit,
      },
    },
    autoResetPageIndex: false,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableRowSelection: !!onSelectionChange,
    getRowId: (row) => row.id,
    defaultColumn: {
      width: "auto",
      minSize: 40,
      maxSize: 1000,
    },
    state: {
      pagination: {
        pageIndex: Math.floor(offset / limit),
        pageSize: limit,
      },
      sorting,
      columnVisibility,
      columnOrder,
      rowSelection,
    },
    onRowSelectionChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(rowSelection) : updater
      setRowSelection(next)
      if (onSelectionChange) {
        onSelectionChange(Object.keys(next))
      }
    },
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: handlePaginationChange,
    manualPagination: true,
    rowCount: rowCount,
    onColumnVisibilityChange: setColumnVisibility,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  })

  const totalKnown = rowCount > 0
  const canGoPrev = table.getState().pagination.pageIndex > 0
  const canGoNext = totalKnown ? table.getCanNextPage() : hasMore

  // When no columns have been resized, clear the snapshot so layout stays fluid
  const columnSizing = table.getState().columnSizing
  const hasResized = Object.keys(columnSizing).length > 0
  if (!hasResized) baseColWidthRef.current = null

  // Sum of all columns using their resized size or the pre-resize base width
  const totalTableWidth =
    hasResized && baseColWidthRef.current
      ? table
          .getVisibleLeafColumns()
          .reduce(
            (sum, col) =>
              sum +
              (columnSizing[col.id] ? col.getSize() : baseColWidthRef.current),
            0,
          )
      : null

  const [jumpToPageValue, setJumpToPageValue] = React.useState(
    table ? table.getState().pagination.pageIndex + 1 : 1,
  )

  React.useEffect(() => {
    setJumpToPageValue(table.getState().pagination.pageIndex + 1)
  }, [table.getState().pagination.pageIndex])

  // Clear selection whenever the data changes (page turn, filter change)
  React.useEffect(() => {
    setRowSelection({})
    if (onSelectionChange) onSelectionChange([])
  }, [data])

  return (
    <VStack
      direction="column"
      flexGrow={1}
      minHeight={0}
      spacing="0"
      align="stretch"
    >
      {isLoading ? (
        <Box width="100%" maxWidth="100%" overflow="hidden" flex="1">
          <LoadingDataDisplay />
        </Box>
      ) : (data == null || data == undefined || data?.length == 0) &&
        SchemaRecordCounts.getValue()[schemaId] !== undefined &&
        SchemaRecordCounts.getValue()[schemaId] > 0 ? (
        <Box width="100%" maxWidth="100%" overflow="hidden" flex="1">
          <AllRecordsFilteredOutDisplay />
        </Box>
      ) : hasProxy && data?.length === 0 ? (
        <Box width="100%" maxWidth="100%" overflow="auto" flex="1" mt={4}>
          <HasProxyDataDisplay
            uploadState={uploadState}
            onUploadStateChange={onUploadStateChange}
            onUpdateRecords={onUpdateRecords}
            fileDropProps={{
              fileInputRef,
              selectedFile,
              onFileChange,
              onFileDrop,
              onOpenAttachment,
              onClearFile,
            }}
          />
        </Box>
      ) : (data == null || data == undefined || data?.length == 0) &&
        (SchemaRecordCounts.getValue()[schemaId] === undefined ||
          SchemaRecordCounts.getValue()[schemaId] === 0) ? (
        <Box width="100%" maxWidth="100%" overflow="hidden" flex="1">
          <NoDataDisplay />
        </Box>
      ) : (
        <>
          {/* table controls */}
          <HStack
            mt={2}
            bg="white"
            px={3}
            py={1.5}
            borderRadius="md md 0 0"
            w="full"
            gap={3}
            flexWrap="wrap"
            flexShrink={0}
          >
            {/* left: go-to + rows-per-page */}
            <HStack flexShrink={0} fontSize="sm" spacing={2}>
              <Text whiteSpace="nowrap" color="Gray.600">
                Go to:
              </Text>
              <Input
                minW={10}
                maxW={16}
                height={7}
                type="number"
                borderRadius="sm"
                defaultValue={table.getState().pagination.pageIndex + 1}
                value={
                  jumpToPageValue ?? table.getState().pagination.pageIndex + 1
                }
                onChange={(e) => {
                  setJumpToPageValue(e.target.value)
                }}
                onBlur={() => {
                  let page = Number(jumpToPageValue) - 1
                  if (isNaN(page)) page = 0
                  if (page < 0) page = 0
                  const maxPage = table.getPageCount()
                  if (maxPage > 0 && page > maxPage - 1) page = maxPage - 1
                  handlePaginationChange({
                    pageIndex: page,
                    pageSize: table.getState().pagination.pageSize,
                  })
                  setJumpToPageValue(page + 1)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    let page = Number(jumpToPageValue) - 1
                    if (isNaN(page)) page = 0
                    if (page < 0) page = 0
                    const maxPage = table.getPageCount()
                    if (maxPage > 0 && page > maxPage - 1) page = maxPage - 1
                    handlePaginationChange({
                      pageIndex: page,
                      pageSize: table.getState().pagination.pageSize,
                    })
                    setJumpToPageValue(page + 1)
                  }
                }}
                size="sm"
              />
              <Select
                height={7}
                borderRadius="sm"
                maxW={32}
                minW="fit-content"
                fontSize="sm"
                value={table.getState().pagination.pageSize}
                onChange={handlePageSizeChange}
              >
                {DEFAULT_PAGES.map((pageSize, index) => (
                  <option key={`page-${index}`} value={pageSize}>
                    Show {pageSize} rows
                  </option>
                ))}
              </Select>
            </HStack>

            {/* center: page nav + record count — grows to fill, centers content */}
            <HStack
              flex={1}
              minW="fit-content"
              justify="center"
              fontSize="sm"
              spacing={1}
            >
              <Button
                size="sm"
                variant="table"
                onClick={() =>
                  handlePaginationChange({
                    pageIndex: 0,
                    pageSize: table.getState().pagination.pageSize,
                  })
                }
                isDisabled={!canGoPrev}
              >
                <ArrowBackIcon />
              </Button>
              <Button
                size="sm"
                variant="table"
                onClick={() =>
                  handlePaginationChange({
                    pageIndex: table.getState().pagination.pageIndex - 1,
                    pageSize: table.getState().pagination.pageSize,
                  })
                }
                isDisabled={!canGoPrev}
              >
                <ChevronLeftIcon />
              </Button>
              <Text minW="fit-content" px={1}>
                {totalKnown && !isCountLoading
                  ? `Page ${
                      table.getState().pagination.pageIndex + 1
                    } / ${getNumformat(table.getPageCount())}`
                  : `Page ${table.getState().pagination.pageIndex + 1}`}
              </Text>
              <Button
                size="sm"
                variant="table"
                onClick={() =>
                  handlePaginationChange({
                    pageIndex: table.getState().pagination.pageIndex + 1,
                    pageSize: table.getState().pagination.pageSize,
                  })
                }
                isDisabled={!canGoNext}
              >
                <ChevronRightIcon />
              </Button>
              <Button
                size="sm"
                variant="table"
                onClick={() =>
                  handlePaginationChange({
                    pageIndex: table.getPageCount() - 1,
                    pageSize: table.getState().pagination.pageSize,
                  })
                }
                isDisabled={!totalKnown || !canGoNext || isCountLoading}
              >
                <ArrowForwardIcon />
              </Button>
              <Text color="Gray.500" ml={2} whiteSpace="nowrap">
                {table.getState().pagination.pageIndex === 0
                  ? 1
                  : table.getState().pagination.pageIndex *
                      table.getState().pagination.pageSize +
                    1}
                –
                {totalKnown
                  ? (table.getState().pagination.pageIndex + 1) *
                      table.getState().pagination.pageSize <
                    table.getRowCount()
                    ? (table.getState().pagination.pageIndex + 1) *
                      table.getState().pagination.pageSize
                    : table.getRowCount()
                  : table.getState().pagination.pageIndex *
                      table.getState().pagination.pageSize +
                    (data?.length || 0)}
                {isLoading || isCountLoading
                  ? " of "
                  : totalKnown
                    ? ` of ${getNumformat(table.getRowCount())} `
                    : " "}
                {isLoading || isCountLoading ? (
                  <Spinner
                    size="xs"
                    ml={1}
                    mr={1}
                    display="inline-block"
                    verticalAlign="middle"
                  />
                ) : null}
                records
              </Text>
            </HStack>

            {/* right: encrypt */}
            <Box flexShrink={0}>
              <EncryptButton />
            </Box>
          </HStack>
          {/* table header row */}
          <TableContainer
            ref={tableContainerRef}
            flex="1"
            minHeight={0}
            height="100%"
            overflowY="auto"
            overflowX="auto"
            bg="white"
            width="100%"
            maxW="100%"
            borderRadius="0 0 md md"
            boxShadow="lg"
          >
            <CKTable
              size="sm"
              width="100%"
              variant="dataTable"
              sx={{
                tableLayout: "fixed",
                width: totalTableWidth ? `${totalTableWidth}px` : "100%",
                minWidth: "100%",
              }}
            >
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => {
                  return (
                    <Tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const meta = header.column.columnDef
                        return (
                          <Th
                            key={header.id}
                            isNumeric={meta?.isNumeric}
                            colSpan={header.colSpan}
                            role="group"
                            onDragOver={(e) => {
                              e.preventDefault()
                              if (
                                dragColumnId.current &&
                                dragColumnId.current !== header.column.id
                              ) {
                                setDragOverColumnId(header.column.id)
                              }
                            }}
                            onDragLeave={() => setDragOverColumnId(null)}
                            onDrop={(e) => {
                              e.preventDefault()
                              setDragOverColumnId(null)
                              const fromId = dragColumnId.current
                              const toId = header.column.id
                              if (!fromId || fromId === toId) return
                              setColumnOrder((prev) => {
                                const newOrder = [...prev]
                                const fromIdx = newOrder.indexOf(fromId)
                                const toIdx = newOrder.indexOf(toId)
                                if (fromIdx === -1 || toIdx === -1) return prev
                                newOrder.splice(fromIdx, 1)
                                newOrder.splice(toIdx, 0, fromId)
                                return newOrder
                              })
                              dragColumnId.current = null
                            }}
                            sx={{
                              position: "relative",
                              width: columnSizing[header.column.id]
                                ? `${header.getSize()}px`
                                : baseColWidthRef.current
                                  ? `${baseColWidthRef.current}px`
                                  : "auto",
                              maxWidth: "500px",
                              minWidth: "40px",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                              textTransform: "none",
                              bg:
                                dragOverColumnId === header.column.id
                                  ? "Gray.100"
                                  : undefined,
                              transition: "background 0.15s",
                            }}
                          >
                            <HStack
                              minH="unset"
                              alignItems="center"
                              onClick={header.column.getToggleSortingHandler()}
                              cursor="pointer"
                              gap={1}
                              w="full"
                              sx={{ paddingY: "0 !important" }}
                            >
                              <Text
                                isTruncated
                                width="100%"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                                lineHeight="1"
                                color="Gray.600"
                                title={
                                  header.isPlaceholder
                                    ? ""
                                    : String(
                                        flexRender(
                                          header.column.columnDef.header,
                                          header.getContext(),
                                        ),
                                      )
                                }
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                      header.column.columnDef.header,
                                      header.getContext(),
                                    )}
                              </Text>
                              {header.column.getIsSorted() &&
                                (header.column.getIsSorted() === "desc" ? (
                                  <TriangleDownIcon
                                    flexShrink={0}
                                    aria-label="sorted descending"
                                  />
                                ) : (
                                  <TriangleUpIcon
                                    flexShrink={0}
                                    aria-label="sorted ascending"
                                  />
                                ))}
                            </HStack>
                            {/* resize handle — absolute, no layout impact */}
                            {header.column.getCanResize() && (
                              <Box
                                onDoubleClick={() => header.column.resetSize()}
                                onMouseDown={(e) => {
                                  if (!baseColWidthRef.current) {
                                    const containerWidth =
                                      tableContainerRef.current?.offsetWidth ||
                                      0
                                    const colCount =
                                      table.getVisibleLeafColumns().length
                                    baseColWidthRef.current =
                                      colCount > 0
                                        ? Math.floor(containerWidth / colCount)
                                        : 100
                                  }
                                  header.getResizeHandler()(e)
                                }}
                                onTouchStart={(e) => {
                                  if (!baseColWidthRef.current) {
                                    const containerWidth =
                                      tableContainerRef.current?.offsetWidth ||
                                      0
                                    const colCount =
                                      table.getVisibleLeafColumns().length
                                    baseColWidthRef.current =
                                      colCount > 0
                                        ? Math.floor(containerWidth / colCount)
                                        : 100
                                  }
                                  header.getResizeHandler()(e)
                                }}
                                className={`resizer ${
                                  header.column.getIsResizing()
                                    ? "isResizing"
                                    : ""
                                }`}
                                sx={{
                                  position: "absolute",
                                  right: 0,
                                  top: 0,
                                  height: "100%",
                                  width: "6px",
                                  cursor: "col-resize",
                                  zIndex: 2,
                                  userSelect: "none",
                                  background: header.column.getIsResizing()
                                    ? "Secondary.500"
                                    : "transparent",
                                  transition: "background 0.2s",
                                }}
                              />
                            )}
                            {/* drag handle — absolute, no layout impact */}
                            <Box
                              as="span"
                              draggable
                              onDragStart={(e) => {
                                dragColumnId.current = header.column.id
                                e.dataTransfer.effectAllowed = "move"
                                e.stopPropagation()
                              }}
                              onDragEnd={() => {
                                dragColumnId.current = null
                                setDragOverColumnId(null)
                              }}
                              onClick={(e) => e.stopPropagation()}
                              opacity={0}
                              _groupHover={{ opacity: 0.4 }}
                              _hover={{ opacity: "1 !important" }}
                              cursor="grab"
                              color="Gray.400"
                              sx={{
                                position: "absolute",
                                right: "8px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 1,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <FaGripVertical size={12} />
                            </Box>
                            <Box>
                              {filterIsOpen && (
                                <Menu closeOnSelect={false}>
                                  <MenuButton
                                    icon={
                                      header.column.getFilterValue() ===
                                      undefined ? (
                                        <SearchIcon />
                                      ) : (
                                        <Search2Icon />
                                      )
                                    }
                                    isRound={true}
                                    variant="ghost"
                                    colorScheme={
                                      header.column.getFilterValue() ===
                                      undefined
                                        ? "gray"
                                        : "orange"
                                    }
                                    fontSize="xs"
                                    aria-label="column filter"
                                    size="xs"
                                  />
                                  <MenuList p="1">
                                    <Flex w="full" direction="column" gap="1">
                                      {header.column.getCanFilter() && (
                                        <Flex>
                                          <Filter
                                            column={header.column}
                                            table={table}
                                          />
                                        </Flex>
                                      )}
                                      <Button
                                        rightIcon={<FaTrash />}
                                        colorScheme="blue"
                                        variant="ghost"
                                        size="xs"
                                        onClick={() =>
                                          header.column.setFilterValue(
                                            undefined,
                                          )
                                        }
                                      >
                                        Reset
                                      </Button>
                                    </Flex>
                                  </MenuList>
                                </Menu>
                              )}
                            </Box>
                          </Th>
                        )
                      })}
                    </Tr>
                  )
                })}
              </Thead>
              <Tbody>
                {table.getRowModel().rows?.map((row) => (
                  <Tr key={row.id} _hover={{ shadow: "md" }}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <Td
                          key={cell.id}
                          sx={{
                            width: columnSizing[cell.column.id]
                              ? `${cell.column.getSize()}px`
                              : baseColWidthRef.current
                                ? `${baseColWidthRef.current}px`
                                : "auto",
                            maxWidth: "500px",
                            minWidth: "40px",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Td>
                      )
                    })}
                  </Tr>
                ))}
              </Tbody>
            </CKTable>
          </TableContainer>
        </>
      )}
    </VStack>
  )
}

// Filter Component
const Filter = ({ column, table }) => {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id)

  const columnFilterValue = column.getFilterValue()

  const columnType = getType(firstValue)
  const facets = column?.getFacetedUniqueValues()
  // Custom facets if keys is array then sum the equal value and remove duplicate
  const customFacets = new Map()
  for (const [key, value] of facets) {
    if (Array.isArray(key)) {
      for (const k of key) {
        const prevValue = customFacets.get(k) || 0
        customFacets.set(k, prevValue + value)
      }
    } else {
      const prevValue = customFacets.get(key) || 0
      customFacets.set(key, prevValue + value)
    }
  }

  const sortedUniqueValues = React.useMemo(
    () =>
      columnType === "number" || columnType === "date"
        ? []
        : Array.from(customFacets.keys()).sort(),
    [customFacets],
  )

  const meta = column.columnDef.meta
  const switchEval =
    meta && meta.columnType
      ? meta.columnType.toLowerCase()
      : getType(firstValue)

  switch (switchEval) {
    case "number":
      return (
        <HStack w="full" spacing={1}>
          <Input
            type="number"
            size="sm"
            min={Number(
              column.getFacetedMinMaxValues()?.[0] !== undefined &&
                column.getFacetedMinMaxValues()?.[0] !== null
                ? column.getFacetedMinMaxValues()?.[0]
                : "",
            )}
            max={Number(
              column.getFacetedMinMaxValues()?.[1] !== undefined &&
                column.getFacetedMinMaxValues()?.[1] !== null
                ? column.getFacetedMinMaxValues()?.[1]
                : "",
            )}
            value={columnFilterValue?.[0] ?? ""}
            onChange={(e) =>
              column.setFilterValue((old) => [e.target.value, old?.[1]])
            }
            placeholder={`min ${
              column.getFacetedMinMaxValues()?.[0] !== undefined &&
              column.getFacetedMinMaxValues()?.[0] !== null
                ? `(${column.getFacetedMinMaxValues()?.[0]})`
                : ""
            }`}
          />
          <Input
            type="number"
            size="sm"
            min={Number(
              column.getFacetedMinMaxValues()?.[0] !== undefined &&
                column.getFacetedMinMaxValues()?.[0] !== null
                ? column.getFacetedMinMaxValues()?.[0]
                : "",
            )}
            max={Number(
              column.getFacetedMinMaxValues()?.[1] !== undefined &&
                column.getFacetedMinMaxValues()?.[1] !== null
                ? column.getFacetedMinMaxValues()?.[1]
                : "",
            )}
            value={columnFilterValue?.[1] ?? ""}
            onChange={(e) => {
              column.setFilterValue((old) => [old?.[0], e.target.value])
            }}
            placeholder={`max ${
              column.getFacetedMinMaxValues()?.[1] !== undefined &&
              column.getFacetedMinMaxValues()?.[1] !== null
                ? `(${column.getFacetedMinMaxValues()?.[1]})`
                : ""
            }`}
          />
        </HStack>
      )

    case "date":
      return (
        <HStack w="full" spacing={1}>
          <SingleDatepicker
            name="start-date-input"
            date={columnFilterValue?.[0] ?? undefined}
            onDateChange={(newStartDate) =>
              column.setFilterValue((old) => [newStartDate, old?.[1]])
            }
            propsConfigs={{
              inputProps: {
                size: "sm",
                placeholder: "from...",
              },
            }}
            configs={{
              dateFormat: "yyyy-MM-dd",
            }}
          />

          <SingleDatepicker
            name="end-date-input"
            date={columnFilterValue?.[1] ?? undefined}
            onDateChange={(newEndDate) =>
              column.setFilterValue((old) => [old?.[0], newEndDate])
            }
            propsConfigs={{
              inputProps: {
                size: "sm",
                placeholder: "to...",
              },
            }}
            configs={{
              dateFormat: "yyyy-MM-dd",
            }}
          />
        </HStack>
      )

    case "boolean":
      return (
        <HStack w="full" spacing={1}>
          <Select
            id={column.id + "list"}
            placeholder={`select... (${customFacets.size})`}
            size="sm"
            onChange={(e) => {
              column.setFilterValue(
                !e.target.value ? undefined : e.target.value === "true",
              )
            }}
            value={column.getFilterValue() ?? ""}
          >
            {sortedUniqueValues.slice(0, 5000).map((value) => (
              <option value={value} key={value}>
                {value ? "True" : "False"}
              </option>
            ))}
          </Select>
        </HStack>
      )

    case "enum":
      return (
        <Flex w="full">
          <Select
            id={column.id + "list"}
            placeholder={`select... (${customFacets.size})`}
            size="sm"
            onChange={(e) => column.setFilterValue(e.target.value)}
            value={column.getFilterValue() || ""}
          >
            {sortedUniqueValues.slice(0, 5000).map((value) => (
              <option value={value} key={value}>
                {value}
              </option>
            ))}
          </Select>
        </Flex>
      )

    case "multienum":
      return (
        <Flex w="full">
          <CheckboxGroup
            defaultValue={sortedUniqueValues.slice(0, 5000)}
            onChange={(e) => column.setFilterValue(e)}
            value={column.getFilterValue() || sortedUniqueValues.slice(0, 5000)}
          >
            <VStack align="start">
              {sortedUniqueValues.slice(0, 5000).map((value) => (
                <Checkbox value={value} key={value}>
                  {value}
                </Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
        </Flex>
      )

    case "array":
      return (
        <Flex w="full">
          <CheckboxGroup
            defaultValue={sortedUniqueValues.slice(0, 5000)}
            onChange={(e) => column.setFilterValue(e)}
            value={column.getFilterValue() || sortedUniqueValues.slice(0, 5000)}
          >
            <VStack align="start">
              {sortedUniqueValues.slice(0, 5000).map((value) => (
                <Checkbox value={value} key={value}>
                  {value}
                </Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
        </Flex>
      )

    case "string":
      return (
        <Flex w="full">
          <Input
            type="text"
            size="sm"
            value={columnFilterValue ?? ""}
            onChange={(e) => column.setFilterValue(e.target.value)}
            placeholder={`find...`}
            list={column.id + "list"}
          />
        </Flex>
      )

    default:
      return (
        <Flex w="full">
          <Input
            type="text"
            size="sm"
            value={columnFilterValue ?? ""}
            onChange={(e) => column.setFilterValue(e.target.value)}
            placeholder={`find...`}
            list={column.id + "list"}
          />
        </Flex>
      )
  }
}

function getType(variable) {
  if (variable === null) {
    return "null"
  }

  switch (typeof variable) {
    case "string":
      return "string"
    case "number":
      return "number"
    case "boolean":
      return "boolean"
    case "undefined":
      return "undefined"
    case "function":
      return "function"
    case "object":
      if (variable instanceof Date) {
        return "date"
      }
      if (Array.isArray(variable)) {
        return "array"
      }
      return "object"
    default:
      return "unknown"
  }
}

// summary column
export const getSummary = (table, field) => {
  const sum = table
    .getFilteredRowModel()
    .rows.reduce((total, row) => total + row.getValue(field), 0)
  return sum || 0
}
